import { Box, Flex } from "rebass/styled-components";
import styled from "styled-components";

/* The host app's index.css locks body { overflow: hidden } so its internal
   panes manage their own scroll. This standalone install page would clip
   off-viewport content otherwise — claim the full viewport and own scroll
   here so anything below the fold is reachable. */
export const Page = styled(Box)`
  height: 100vh;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  box-sizing: border-box;
  background: radial-gradient(
      circle at 70% 0%,
      rgba(77, 189, 148, 0.12) 0%,
      transparent 55%
    ),
    #0a0a0d;
  color: #fff;
  font-family:
    Inter,
    system-ui,
    -apple-system,
    sans-serif;
  padding: 64px 40px 96px;

  @media (max-width: 720px) {
    padding: 40px 20px 80px;
  }
`;

export const Container = styled(Flex)`
  flex-direction: column;
  gap: 32px;
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  box-sizing: border-box;
`;

export const Header = styled(Flex)`
  flex-direction: column;
  gap: 8px;
`;

export const Eyebrow = styled(Box)`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  color: rgba(77, 189, 148, 0.85);
`;

export const Title = styled(Box)`
  font-size: 32px;
  font-weight: 800;
  letter-spacing: -0.4px;
`;

export const Subtitle = styled(Box)`
  font-size: 15px;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.7);
  max-width: 600px;
`;

export const Card = styled(Box)`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  padding: 24px;
`;

export const StepLabel = styled(Flex)`
  align-items: center;
  gap: 10px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: rgba(255, 255, 255, 0.55);
  margin-bottom: 12px;
`;

export const StepNumber = styled(Box)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(77, 189, 148, 0.18);
  border: 1px solid rgba(77, 189, 148, 0.45);
  color: #4dbd94;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0;
`;

export const DragLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 22px;
  background: linear-gradient(120deg, #4dbd94 0%, #2e8392 100%);
  border-radius: 10px;
  color: #0a0a0d;
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-decoration: none;
  cursor: grab;
  user-select: none;
  box-shadow: 0 8px 24px rgba(77, 189, 148, 0.25);
  transition: transform 120ms ease;

  &:hover {
    transform: translateY(-1px);
  }
  &:active {
    cursor: grabbing;
  }
`;

export const HelpText = styled(Box)`
  font-size: 13px;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.6);
`;

export const InstructionList = styled.ol`
  margin: 0;
  padding-left: 22px;
  font-size: 14px;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.85);

  li + li {
    margin-top: 4px;
  }
  code {
    background: rgba(255, 255, 255, 0.08);
    padding: 1px 6px;
    border-radius: 4px;
    font-family: ui-monospace, "SF Mono", Menlo, monospace;
    font-size: 12.5px;
  }
`;

export const ConfigRow = styled(Flex)`
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
`;

export const ConfigLabel = styled(Box)`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: rgba(255, 255, 255, 0.55);
`;

export const ConfigInput = styled.input`
  width: 100%;
  /* <input> defaults to content-box, so width:100% + padding overflows the
     Card to the right. Force border-box so 100% includes the padding. */
  box-sizing: border-box;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  color: #fff;
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 13px;

  &:focus {
    outline: none;
    border-color: rgba(77, 189, 148, 0.6);
  }
`;

export const CodeBox = styled.pre`
  background: #050507;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 14px;
  margin: 0;
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 11px;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.75);
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 240px;
  overflow-y: auto;
`;

export const CopyButton = styled.button`
  margin-top: 10px;
  padding: 8px 14px;
  background: rgba(77, 189, 148, 0.12);
  border: 1px solid rgba(77, 189, 148, 0.35);
  border-radius: 6px;
  color: #4dbd94;
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  letter-spacing: 0.3px;

  &:hover {
    background: rgba(77, 189, 148, 0.18);
  }
`;
